/**
 * Outlook / Drift Risk component.
 *
 * "Are work phases drifting behind schedule?"
 *
 * Consumes the existing PhaseSummaryTimeline computation (the WARNING_GAP =
 * 15 percentage-points elapsed-vs-actual heuristic, see
 * `src/Components/KpiNavigation/PhaseSummaryTimeline.tsx:74-88`). The engine
 * doesn't recompute phase status — it consumes the same status values the
 * timeline already shows in the UI, ensuring the reliability signal aligns
 * with what users see on the Phase tab.
 *
 * Score formula: weighted penalty per phase status.
 *   green / on-track / not-started → 0 penalty
 *   warning                        → 1 penalty
 *   critical                       → 2 penalty (twice as bad)
 *   complete                       → 0 penalty (already done)
 *
 *   penalty_ratio = (warning_count + 2 * critical_count) / phase_count
 *   score = max(0, 100 - penalty_ratio * 100)
 *
 * Lifecycle: applicable when project has any phases configured. A project
 * with no phases (less rigorous tier) returns N/A — not penalized for
 * absence of phase data, since some projects legitimately don't track them.
 *
 * See: DEV-846 epic, DEV-847.
 */

import type { ComponentResult, DataReliabilityInputs } from '../types';

export function computeDriftRisk(
  inputs: DataReliabilityInputs,
): ComponentResult {
  const phases = inputs.phase_summary;

  if (phases.phase_count <= 0) {
    return {
      key: 'drift_risk',
      score: null,
      applicable: false,
      reason: 'No phases configured — drift cannot be measured',
      explain: 'Project has no phase tracking.',
    };
  }

  const penaltyUnits = phases.warning_count + 2 * phases.critical_count;
  // Cap penalty at phase_count so score never goes negative.
  // (penalty_ratio of 1.0 = every phase is critical, score 0.)
  const penaltyRatio = Math.min(1, penaltyUnits / phases.phase_count);
  const score = Math.round(100 - penaltyRatio * 100);

  let explain: string;
  if (phases.warning_count === 0 && phases.critical_count === 0) {
    explain = `All ${phases.phase_count} phase${phases.phase_count === 1 ? ' is' : 's are'} on track.`;
  } else {
    const parts: string[] = [];
    if (phases.warning_count > 0) {
      parts.push(`${phases.warning_count} warning`);
    }
    if (phases.critical_count > 0) {
      parts.push(`${phases.critical_count} critical`);
    }
    explain = `${parts.join(' + ')} of ${phases.phase_count} phases drifting.`;
  }

  return {
    key: 'drift_risk',
    score,
    applicable: true,
    explain,
  };
}
